import { Text, mergeClasses } from "@fluentui/react-components";
import { HostClientType } from "@microsoft/teams-js";
import { Flex } from "../flex";
import { useStyles } from "./style";
import { VoiceInteractionProps } from "./types";
import {
	TeamsContextState,
	useTeamsContextStore,
} from "@/common/stores/nonpersistingStores/teamsContext";
import { useWindowAnchoredBottomRight } from "@/hooks/window";
import { OfferingVariableTextView } from "@/components/atoms/offeringVariableTextView";
import { SkillsDisplay } from ".";
import {
	InteractionOfferingDescription,
	OfferingSourceType,
} from "@/components/atoms/interactionOfferingDescription";

export const VoiceDetailsCard = (props: VoiceInteractionProps) => {
	const style = useStyles();

	const hostClientType = useTeamsContextStore(
		(state: TeamsContextState) => state.hostClientType
	);
	const isTeamsWeb = hostClientType === HostClientType.web;

	// The stack-above-the-native-toast trick only applies on Windows desktop:
	// the toast pops at the screen's bottom-right, over the app, when the
	// window is anchored there. On macOS notifications appear top-right.
	const isWindowsDesktop = hostClientType === HostClientType.desktop;
	const isAnchoredBottomRight = useWindowAnchoredBottomRight(isWindowsDesktop);

	// An undefined hostClientType (context not yet resolved, or app hosted
	// outside Teams) intentionally falls through to the corner-docked desktop
	// layout: outside Teams there is no call toast to align with, and inside
	// Teams the store is populated at app startup — long before a call can
	// render this card — so the card re-renders into place via the store hook.
	const isCornerDocked =
		!props.isNextToInternalToaster &&
		!isTeamsWeb &&
		!(isWindowsDesktop && isAnchoredBottomRight);

	// On desktop the card either docks to the corner or reserves the native
	// toast's footprint (see style.ts).
	const desktopPositionClass = isCornerDocked
		? style.containerVoiceDesktop
		: style.containerVoiceDesktopAnchored;

	// On web the card stacks above the in-app toast.
	const hostPositionClass = isTeamsWeb
		? style.containerVoice
		: desktopPositionClass;

	// Next to the internal toaster the base cardContainer placement applies.
	const positionClass = props.isNextToInternalToaster
		? undefined
		: hostPositionClass;

	const {
		contactName,
		contactInfo,
		queueName,
		skills,
		offeringVariableText,
		offeringVariableTextIsAiGenerated,
		transferInitiatorName,
		fallbackType,
		fallbackFrom,
		callbackQueueName,
		isCallParked,
	} = props;

	const getPreviousActionComponent = () => {
		if (transferInitiatorName) {
			return (
				<InteractionOfferingDescription
					resourceName={transferInitiatorName}
					actionType={OfferingSourceType.Transfer}
				/>
			);
		}

		if (fallbackType !== undefined && fallbackFrom) {
			return (
				<InteractionOfferingDescription
					resourceName={fallbackFrom}
					actionType={OfferingSourceType.Fallback}
				/>
			);
		}

		if (isCallParked && queueName) {
			return (
				<InteractionOfferingDescription
					resourceName={queueName}
					actionType={OfferingSourceType.Park}
				/>
			);
		}

		if (callbackQueueName) {
			return (
				<InteractionOfferingDescription
					resourceName={callbackQueueName}
					actionType={OfferingSourceType.Callback}
				/>
			);
		}

		return <></>;
	};

	return (
		<Flex
			className={mergeClasses(style.cardContainer, positionClass)}
			column
			hAlign={isCornerDocked ? "center" : undefined}
		>
			{getPreviousActionComponent()}
			<div className={style.innerContainer}>
				<Text
					align="center"
					weight="bold"
					block
					className={style.whiteText}
					truncate
					wrap={false}
					title={contactName}
				>
					{contactName}
				</Text>
				<span className={style.details}>
					{contactInfo && (
						<Text
							align="center"
							block
							className={style.grayText}
							truncate
							wrap={false}
							title={contactInfo}
						>
							{contactInfo}
						</Text>
					)}
				</span>
				<div className={style.footer}>
					<Text
						align="center"
						block
						className={mergeClasses(style.text, style.grayText)}
						truncate
						wrap={false}
						title={queueName}
					>
						{queueName}
					</Text>
					<SkillsDisplay skills={skills} />
				</div>
				<OfferingVariableTextView offeringVariableText={offeringVariableText} offeringVariableTextIsAiGenerated={offeringVariableTextIsAiGenerated} />
			</div>
		</Flex>
	);
};
